/**
 * Nigerian phone normalization — THE single implementation.
 *
 * This module decides which auth account a person lands in, so there must be
 * exactly one copy of these rules in the app. `toNationalDigits` lived inside
 * app/login/LoginForm.tsx as a module-local function; the admin booking path
 * needed the same logic, and a second implementation that merely *agrees today*
 * is how a customer ends up logging in and not finding their own booking — or
 * finding a stranger's.
 *
 * NOT `server-only`: LoginForm is a Client Component and imports this too. That
 * is the point.
 *
 * THE CONTRACT, traced end to end against live data:
 *
 *     customer types   "0816 945 1442"
 *     toNationalDigits  "8169451442"        10 national digits
 *     toE164            "+2348169451442"    what we send to GoTrue
 *     GoTrue stores     "2348169451442"     no '+' — this is the account key
 *
 * So `toAuthPhone` (no plus) is what matches auth.users.phone, and `toE164` is
 * what bookings.contact_phone stores. Anything that gets this wrong either mints
 * a duplicate account or attaches a booking to the wrong person.
 */

export const NIGERIA_PREFIX = "+234";

/** Exactly the national significant number length for a Nigerian mobile. */
export const NATIONAL_DIGITS = 10;

/**
 * Nigerian mobile allowlist, kept BYTE-IDENTICAL to the copy in
 * supabase/functions/send-sms/lib.ts.
 *
 * It is duplicated rather than imported because that file is Deno code for an
 * Edge Function — it lives outside this app's build and cannot be imported here.
 * scripts/test-phone.mjs asserts the two literals match, the same drift-guard
 * approach used for OTP_SMS_TEMPLATE.
 *
 * Enforcing it here matters: an account minted for a number outside this set is
 * one the SMS hook will later refuse to text, so the person could never log in.
 */
export const NIGERIA_MOBILE_RE = /^\+?234[789][01]\d{8}$/;

/**
 * Normalize a Nigerian number to its 10 national digits, so autofill and
 * paste-from-contacts work as well as local typing:
 *   0803 123 4567 / +234 803 123 4567 / 2348031234567 / 002348031234567 -> 8031234567
 *
 * Moved verbatim from app/login/LoginForm.tsx — do not "improve" it in isolation.
 */
export function toNationalDigits(local: string): string {
  return local
    .replace(/\D/g, "")
    .replace(/^(?:00)?234/, "") // international prefix, with or without 00
    .replace(/^0+/, ""); // trunk zero (also handles "+234 0803 ...")
}

/** True when the input normalizes to a plausible Nigerian mobile. */
export function isValidNigerianMobile(input: string): boolean {
  const national = toNationalDigits(input ?? "");
  if (national.length !== NATIONAL_DIGITS) return false;
  return NIGERIA_MOBILE_RE.test(`${NIGERIA_PREFIX}${national}`);
}

/**
 * E.164 with the plus: what GoTrue is given, and what bookings.contact_phone
 * stores. null when the input is not a valid Nigerian mobile — callers must
 * treat null as a rejection, never as "send it anyway".
 */
export function toE164(input: string): string | null {
  if (!isValidNigerianMobile(input)) return null;
  return `${NIGERIA_PREFIX}${toNationalDigits(input)}`;
}

/**
 * The auth.users.phone form: digits only, no plus. GoTrue strips the '+' when it
 * stores, so this is the value to match on when looking an account up.
 */
export function toAuthPhone(input: string): string | null {
  const e164 = toE164(input);
  return e164 === null ? null : e164.slice(1);
}

/** Display form for confirm dialogs and admin cards: "+234 803 123 4567". */
export function formatPhone(input: string): string {
  const national = toNationalDigits(input ?? "");
  if (national.length !== NATIONAL_DIGITS) return input ?? "";
  return `${NIGERIA_PREFIX} ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
}
